import React, { Dispatch } from 'react';
import { NavigateFunction } from 'react-router-dom';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell } from '@components/common';

import { endOfDayInUTC, formatDate } from '@utils/kpi-utils/date';

import { ECommonRequestStatusesForAssetRequest } from '@enums/asset';

import { formatTemplateDefault, formatTemplateWithTime, timezoneUTC } from '@constants/common';
import { routes } from '@constants/routes';

import RequestsActionsColumnCell from '../RequestsActionsColumnCell';

const {
	adminPanel: {
		index,
		outgoingRequestsPath,
	},
} = routes;

interface IGetColumns {
	openStopRentingAssetModal: () => void;
	setSelectedAssetEndDate: Dispatch<React.SetStateAction<number | null>>;
	navigate: NavigateFunction;
	setSelectedRequestId: Dispatch<React.SetStateAction<number | string>>;
}

export const getColumns = ({
	openStopRentingAssetModal,
	setSelectedAssetEndDate,
	setSelectedRequestId,
	navigate,
}: IGetColumns) => ([
	{
		header: 'Request Id',
		accessorKey: 'requestId',
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const requestId = getValue();

			return <AdaptiveTableCell text={requestId} />;
		},
	},
	{
		header: 'Request Date',
		accessorKey: 'createdAt',
		cell: ({ getValue }: CellContext<any, any>) => {
			const createdAt = getValue();
			const date = formatDate(utcToZonedTime(createdAt, timezoneUTC), formatTemplateWithTime);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Start Rent Date',
		accessorKey: 'startRentDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const startRentDate = getValue();
			const date = formatDate(utcToZonedTime(startRentDate, timezoneUTC), formatTemplateDefault);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'End Rent Date',
		accessorKey: 'endRentDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const endRentDate = getValue();
			const date = formatDate(utcToZonedTime(endRentDate, timezoneUTC), formatTemplateDefault);

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Actual end date',
		accessorKey: 'actualEndRentDate',
		cell: ({ getValue }: CellContext<any, any>) => {
			const actualEndRentDate = getValue();
			const date = actualEndRentDate
				? formatDate(utcToZonedTime(actualEndRentDate, timezoneUTC), formatTemplateDefault)
				: '-';

			return <AdaptiveTableCell text={date} />;
		},
	},
	{
		header: 'Request Status',
		accessorKey: 'requestStatus',
		cell: ({ getValue }: CellContext<any, any>) => {
			const requestStatus = getValue();

			return <AdaptiveTableCell text={requestStatus?.title} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			const startRentDate = row?.original?.startRentDate;
			const endRentDate = row?.original?.endRentDate;
			const actualEndRentDate = row?.original?.actualEndRentDate;
			const today = endOfDayInUTC(new Date());
			const requestDbId = row?.original?.requestDbId;
			const id = row?.original?.id;
			const requestStatus = row?.original?.requestStatus?.name;

			const isRentNotStarted = today < startRentDate;
			const isRentEnded = actualEndRentDate ? today > actualEndRentDate : today > endRentDate;

			const isStopRentAssetDisable = requestStatus === ECommonRequestStatusesForAssetRequest.done
				? isRentNotStarted || isRentEnded
				: true;

			const stopRentingAsset = () => {
				setSelectedAssetEndDate(endRentDate);
				setSelectedRequestId(id);

				openStopRentingAssetModal();
			};

			const handleView = () => {
				if (!requestDbId) return;

				navigate(`${index}${outgoingRequestsPath}/${requestDbId}`);
			};

			return (
				<RequestsActionsColumnCell
					handleView={handleView}
					isStopRentAssetDisable={isStopRentAssetDisable}
					isStopUsingAsset
					stopUsingAsset={stopRentingAsset}
				/>
			);
		},
	},
]);
